import InboxIcon from "@mui/icons-material/Inbox";
import { Box, LinearProgress, Typography } from "@mui/material";
import {
    DataGridPro,
    DataGridProProps,
    GridSlots,
    useGridApiRef,
} from "@mui/x-data-grid-pro";

import { useCallback, useEffect } from "react";

const tableStyles = {
    bgcolor: "background.paper",
    border: 0,
    fontSize: "0.8rem",

    "& .MuiDataGrid-columnHeader": {
        bgcolor: "background.paper",
        borderRadius: "0px !important",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "0.7rem",
        fontWeight: 600,
        textTransform: "uppercase" as const,
        letterSpacing: "0.04em",
        color: "text.secondary",
    },
    "& .MuiDataGrid-filler": {
        bgcolor: "background.paper",
    },

    "& .MuiDataGrid-topContainer": {
        bgcolor: "background.paper",
    },

    "--DataGrid-overlayHeight": "300px",

    ".MuiDataGrid-cell--editable:focus": {
        outline: "solid !important",
        outlineWidth: "1px !important",
    },

    ".MuiDataGrid-cell:focus": {
        outline: "none",
    },

    "& .MuiDataGrid-row": {
        "&:last-of-type .MuiDataGrid-cell": {
            borderBottom: "none",
        },
    },

    "& .MuiTablePagination-spacer": {
        display: "none",
    },
    "& .MuiTablePagination-root": {
        fontSize: "0.75rem",
        color: "text.secondary",
    },
    "& .MuiTablePagination-displayedRows": {
        fontSize: "0.75rem",
    },
    "& .MuiTablePagination-selectLabel": {
        fontSize: "0.75rem",
    },

    // Hide the column separator
    "& .MuiDataGrid-columnSeparator": {
        display: "none",
    },

    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
    },
};

interface ITableProps {
    /**
     * Title of the table.
     *
     */
    title?: string;
    /**
     * Whether to auto size the columns.
     * @default false
     */
    autoSize?: boolean;
    /**
     * Custom component to render when there are no rows.
     * @default NoRowsOverlay
     */
    noRowsOverlay?: React.ComponentType;
    /**
     * Change this value to trigger a column auto-resize.
     * Useful when the table's container width changes (e.g. a sibling panel opens/closes).
     */
    resizeTrigger?: unknown;
}

// This is a custom overlay for when there are no rows (data) in the table
export const NoRowsOverlay = () => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
            }}
        >
            <InboxIcon
                color={"primary"}
                style={{ fontSize: 128, opacity: 0.4 }}
            />
            <Typography variant={"body2"} color="text.secondary">
                No Data Available
            </Typography>
        </div>
    );
};

/**
 * Custom implementation of MUI DataGrid component for our use.
 * Custom styles for the table, and overlays for no data and loading.
 *
 */
export const Table = ({
    rows,
    columns,
    autosizeOptions,
    apiRef,
    sx,
    slots,
    slotProps,
    pageSizeOptions,
    autoSize = false,
    noRowsOverlay,
    resizeTrigger,
    ...other
}: ITableProps & Omit<DataGridProProps, "autosizeOnMount">) => {
    const gridApiRef = useGridApiRef();
    const baseApiRef = apiRef || gridApiRef;

    const handleAutoSize = useCallback(() => {
        if (!autoSize || !baseApiRef.current) return;
        baseApiRef.current.autosizeColumns(autosizeOptions);
    }, [autoSize, baseApiRef, autosizeOptions]);

    // autosize columns on data change
    useEffect(() => {
        setTimeout(() => {
            handleAutoSize();
        }, 100);
    }, [baseApiRef, autoSize, autosizeOptions, rows, handleAutoSize]);

    // autosize columns on columns change
    useEffect(() => {
        handleAutoSize();
    }, [columns, handleAutoSize]);

    // autosize columns on window resize
    useEffect(() => {
        if (!autoSize) return () => {};

        window.addEventListener("resize", handleAutoSize);

        return () => {
            window.removeEventListener("resize", handleAutoSize);
        };
    }, [handleAutoSize, autoSize]);

    // autosize columns when resizeTrigger changes (e.g. sibling panel open/close)
    useEffect(() => {
        if (resizeTrigger) handleAutoSize();
    }, [resizeTrigger, handleAutoSize]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: rows?.length === 0 ? 300 : 0,
            }}
        >
            <DataGridPro
                apiRef={baseApiRef}
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                sx={{ ...tableStyles, ...sx }}
                autosizeOnMount={autoSize}
                autosizeOptions={autosizeOptions}
                getRowHeight={() => "auto"}
                pageSizeOptions={pageSizeOptions || [10, 25, 50]}
                disableMultipleColumnsFiltering
                disableMultipleColumnsSorting
                slots={{
                    noRowsOverlay: noRowsOverlay || NoRowsOverlay,
                    loadingOverlay:
                        LinearProgress as GridSlots["loadingOverlay"],
                    ...slots,
                }}
                slotProps={{
                    filterPanel: {
                        filterFormProps: {
                            operatorInputProps: {
                                disabled: true,
                                sx: { display: "none" },
                            },
                        },
                    },
                    ...slotProps,
                }}
                pagination
                {...other}
            />
        </Box>
    );
};
